import './api/interceptors';
import dynamic from 'next/dynamic';
import type { Experiments } from '@carvana/experiment/SharedTypes';
import { DehydratedState, Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactElement, useEffect, useState } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import styled from 'styled-components';
import classnames from 'classnames';
import { useRouter } from 'next/router';
import { v4 } from 'uuid';

import CvtExperimentProvider from './experiments/CvtExperimentProvider';
import Cookie from './shared/Cookie';
import mfeConfig from './mfeConfig';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import AuthProvider from './context/AuthProvider';

const Header = dynamic(() => import('./components/Header'));
const Footer = dynamic(() => import('./components/Footer'));

mfeConfig();

const DisplayController = styled.div`
  &.transposeContent {
    display: none;
  }

  @media print {
    display: none;
  }
`;

const AppProviders = ({
  children,
  cookies,
  pageProps
}: {
  children: ReactElement;
  cookies: string;
  pageProps: {
    dehydratedState: DehydratedState;
    experiments?: Experiments;
    browserCookieId?: string;
  };
}) => {
  const { dehydratedState, experiments = {} } = pageProps;
  const [queryClient] = useState(() => new QueryClient());
  const router = useRouter();
  const [transposeContent, setTransposeContent] = useState(false);

  useEffect(() => {
    import('./shared/datadog').then(module => {
      module.initializeDataDog(experiments);
    });
  }, []);

  useEffect(() => {
    const urlsToTranspose = ['/get-value', '/edit'];
    const shouldTranspose = urlsToTranspose.some(url => router.pathname.includes(url));
    setTransposeContent(shouldTranspose);

    const browserCookie = Cookie.getBcId();
    const dev = process.env.NODE_ENV === 'development';
    if (dev && !browserCookie) {
      Cookie.setCookie('BrowserCookieId', v4(), 30);
    }
  }, []);

  return (
    <ErrorBoundary>
      <CvtExperimentProvider preloadedExperiments={experiments}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools />
          <DisplayController className={classnames({ transposeContent })}>
            <Header cookies={cookies} />
          </DisplayController>
          <AuthProvider>
            <Hydrate state={dehydratedState}>{children}</Hydrate>
          </AuthProvider>
          <DisplayController className={classnames({ transposeContent })}>
            <Footer />
          </DisplayController>
        </QueryClientProvider>
      </CvtExperimentProvider>
    </ErrorBoundary>
  );
};

export default AppProviders;
